import {
  AdAnalyticsDailyPerformance,
  AdAnalyticsFilter,
  AdAnalyticsPlacementTypes,
  AdAnalyticsVideo,
} from '@kaizenplatform/hugot-entity';
import { History } from 'history';
import { List } from 'immutable';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import WithTotalCountArrayPageInfo from '../entity/WithTotalCountArrayPageInfo';
import { OrganizationDetailFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import AdAnalyticsDailyPerformanceWebApi from '../service/persistent/AdAnalyticsDailyPerformanceWebApi';
import AdAnalyticsPlacementTypesWebApi from '../service/persistent/AdAnalyticsPlacementTypesWebApi';
import AdAnalyticsReportWebApi from '../service/persistent/AdAnalyticsReportWebApi';
import AdAnalyticsVideoWebApi from '../service/persistent/AdAnalyticsVideoWebApi';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import {
  disposeAdAnalyticsPlacementTypes,
  finishFetchingAdAnalyticsPlacementTypes,
  setAdAnalyticsPlacementTypes,
  startFetchingAdAnalyticsPlacementTypes,
  updateSelectedPlacementTypes,
} from '../thunk/adAnalyticsPlacementTypes';
import {
  disposeAdAnalyticsVideos,
  finishFetchingAdAnalyticsVideos,
  setAdAnalyticsDailyPerformances,
  setAdAnalyticsVideos,
  startFetchingAdAnalyticsVideos,
  updateSelectedVideos,
} from '../thunk/adAnalyticsVideo';
import { setSubmitReport } from '../thunk/adAnalyticsVideo';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';

class ShowAdAnalyticsVideoUsecase {
  private readonly store: Store<State>;
  private readonly history: History;
  private readonly errorPresenter: ErrorPresenter;
  private readonly messageDispatcher: MessageDispatcher;
  private readonly adAnalyticsVideoWebApi: AdAnalyticsVideoWebApi;
  private readonly adAnalyticsPlacementTypesWebApi: AdAnalyticsPlacementTypesWebApi;
  private readonly adAnalyticsDailyPerformanceWebApi: AdAnalyticsDailyPerformanceWebApi;
  private readonly organizationWebApi: OrganizationWebApi;
  private readonly adAnalyticsReportWebApi: AdAnalyticsReportWebApi;

  constructor({
    store,
    history,
    errorPresenter,
    messageDispatcher,
    adAnalyticsVideoWebApi,
    adAnalyticsPlacementTypesWebApi,
    adAnalyticsDailyPerformanceWebApi,
    organizationWebApi,
    adAnalyticsReportWebApi,
  }: {
    store: Store<State>;
    history: History;
    errorPresenter: ErrorPresenter;
    messageDispatcher: MessageDispatcher;
    adAnalyticsVideoWebApi: AdAnalyticsVideoWebApi;
    adAnalyticsPlacementTypesWebApi: AdAnalyticsPlacementTypesWebApi;
    adAnalyticsDailyPerformanceWebApi: AdAnalyticsDailyPerformanceWebApi;
    organizationWebApi: OrganizationWebApi;
    adAnalyticsReportWebApi: AdAnalyticsReportWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.errorPresenter = errorPresenter;
    this.messageDispatcher = messageDispatcher;
    this.adAnalyticsVideoWebApi = adAnalyticsVideoWebApi;
    this.adAnalyticsPlacementTypesWebApi = adAnalyticsPlacementTypesWebApi;
    this.adAnalyticsDailyPerformanceWebApi = adAnalyticsDailyPerformanceWebApi;
    this.organizationWebApi = organizationWebApi;
    this.adAnalyticsReportWebApi = adAnalyticsReportWebApi;
  }

  public prepare = async (): Promise<void> => {
    this.store.dispatch(startFetchingAdAnalyticsVideos());
    this.store.dispatch(startFetchingAdAnalyticsPlacementTypes());

    const { creativeID } = this.getQueryParameters();

    let items: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };
    let placementTypes: AdAnalyticsPlacementTypes;
    let dailyPerformance: List<AdAnalyticsDailyPerformance>;

    const query = parse(this.store.getState().location.search.slice(1));
    const organizationId = query.org || null;
    let organization: OrganizationDetailFragment | null = null;

    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period,
    };

    try {
      if (organizationId) {
        organization = await this.organizationWebApi.getById(organizationId);
        if (organization) this.store.dispatch(setLastAccessedOrganization(organization));
      }
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
      return;
    }

    try {
      items = await this.fetchAdAnalyticsVideos({ ...properties, adVideoIds: creativeID });
      placementTypes = await this.featchAdAnalyticsPlacementTypes();
      dailyPerformance = await this.featchAdAnalyticsDailyPerformance({ adVideoIds: creativeID });
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.store.dispatch(disposeAdAnalyticsVideos());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    this.store.dispatch(setAdAnalyticsVideos(items.adAnalyticsVideos, items.pageInfo));
    this.store.dispatch(setAdAnalyticsPlacementTypes(placementTypes));
    this.store.dispatch(setAdAnalyticsDailyPerformances(dailyPerformance));
    this.store.dispatch(finishFetchingAdAnalyticsVideos());
    this.store.dispatch(finishFetchingAdAnalyticsPlacementTypes());
    this.store.dispatch(updateSelectedVideos(items.adAnalyticsVideos));
  };

  public updatePlacementTypes = async ({
    platformKey,
    mediaKey,
    placementKey,
    period,
  }: AdAnalyticsFilter): Promise<void> => {
    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: platformKey ? platformKey : selectedPlacementTypes.platformKey,
      mediaKey: mediaKey ? mediaKey : selectedPlacementTypes.mediaKey,
      placementKey: placementKey ? placementKey : selectedPlacementTypes.placementKey,
      period: period ? period : selectedPlacementTypes.period,
    };

    this.store.dispatch(updateSelectedPlacementTypes(properties));
  };

  public submitPlacementTypes = async (): Promise<void> => {
    this.store.dispatch(disposeAdAnalyticsVideos());
    this.store.dispatch(startFetchingAdAnalyticsVideos());
    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const { creativeID } = this.getQueryParameters();
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period,
    };

    let items: { adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo };
    let dailyPerformance: List<AdAnalyticsDailyPerformance>;

    try {
      items = await this.fetchAdAnalyticsVideos({
        ...properties,
        ...{
          adVideoIds: creativeID,
        },
      });
      dailyPerformance = await this.featchAdAnalyticsDailyPerformance({ adVideoIds: creativeID });
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsVideos());
      this.errorPresenter.showAsFatal(err);
      return;
    }
    this.store.dispatch(setAdAnalyticsVideos(items.adAnalyticsVideos, items.pageInfo));
    this.store.dispatch(setAdAnalyticsDailyPerformances(dailyPerformance));
    this.store.dispatch(finishFetchingAdAnalyticsVideos());
  };

  public getQueryParameters = () => {
    // IMO: orgを含まない文字列きたら落ちるのでリファクタした方が良さそう
    const query = parse(this.store.getState().location.search.slice(1));
    const organizationID = query.org;
    const creativeID: string[] = query.creative ? query.creative.split(',') : [];

    return { organizationID, creativeID };
  };

  public fetchAdAnalyticsVideos = async ({
    platformKey,
    mediaKey,
    placementKey,
    period = 'lifetime',
    adVideoIds = null,
  }: AdAnalyticsFilter & {
    adVideoIds?: string[] | null;
  }): Promise<{ adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo }> => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    return await this.adAnalyticsVideoWebApi.getAllOfAdAnalyticsVideo({
      platformKey,
      mediaKey,
      placementKey,
      period,
      adVideoIds,
      filters: [],
      organizationId: organization ? organization.id : '',
    });
  };
  public featchAdAnalyticsPlacementTypes = async (): Promise<AdAnalyticsPlacementTypes> =>
    await this.adAnalyticsPlacementTypesWebApi.getAdAnalyticsPlacementTypes();

  public featchAdAnalyticsDailyPerformance = async ({
    platformKey,
    mediaKey,
    placementKey,
    adVideoIds = null,
  }: AdAnalyticsFilter & {
    adVideoIds?: string[] | null;
  }): Promise<List<AdAnalyticsDailyPerformance>> => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    return await this.adAnalyticsDailyPerformanceWebApi.getAdAnalyticsDailyPerformance({
      platformKey,
      mediaKey,
      placementKey,
      adVideoIds,
      organizationId: organization ? organization.id : '',
    });
  };

  public backListPage = () => {
    this.history.push(`/ad-creatives`);
  };

  public tryCreateReport = async ({ title }: { title: string }) => {
    const { selectedPlacementTypes } = this.store.getState().adAnalyticsPlacementTypes;
    const properties = {
      platformKey: selectedPlacementTypes.platformKey,
      mediaKey: selectedPlacementTypes.mediaKey,
      placementKey: selectedPlacementTypes.placementKey,
      period: selectedPlacementTypes.period ? selectedPlacementTypes.period : 'lifetime',
    };

    const { creativeID } = this.getQueryParameters();

    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    try {
      const id = await this.adAnalyticsReportWebApi.createReport({
        adVideoIds: creativeID,
        title,
        period: properties.period,
        platformKey: properties.platformKey,
        mediaKey: properties.mediaKey,
        placementKey: properties.placementKey,
        organizationId: organization ? organization.id : '',
      });
      this.messageDispatcher.notifyUnimportant('message.report_create');
      this.history.push(`/analytics-report/?${organization ? `org=${organization.id}&` : ''}id=${id}`);
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
    }
    this.store.dispatch(setSubmitReport(true));
  };

  public checkSubmitReport = (): void => {
    this.store.dispatch(setSubmitReport(false));
  };

  public dispose = (): void => {
    this.store.dispatch(disposeAdAnalyticsVideos());
    this.store.dispatch(disposeAdAnalyticsPlacementTypes());
  };

  // Mock
  // ---------------------------------------------------------
  public fetchAdAnalyticsVideosMock = async (): Promise<{
    adAnalyticsVideos: List<AdAnalyticsVideo>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => await this.adAnalyticsVideoWebApi.getAllOfAdAnalyticsVideoMock();

  public featchAdAnalyticsPlacementTypesMock = async (): Promise<AdAnalyticsPlacementTypes> =>
    await this.adAnalyticsPlacementTypesWebApi.getAdAnalyticsPlacementTypesMock();
}

export default ShowAdAnalyticsVideoUsecase;
